import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  Attachment,
  AttachmentResponse,
  AttachmentsResponse,
  IntendedAttachableType,
} from '../types/attachment';
import type { EditableAttachment, PendingAttachment } from '../types/attachmentsForm';
import {
  getPendingDisplayName,
  isEditableAttachmentDirty,
  isStagedNoteDirty,
} from '../types/attachmentsForm';

export type AttachmentParentType = 'invoice' | 'bill' | 'item' | 'payment' | 'bill_payment';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parentAttachmentsPath = (parent: AttachmentParentType, parentId: number): string => {
  const base =
    parent === 'invoice'
      ? API_CONFIG.ENDPOINTS.INVOICES.GET_BY_ID
      : parent === 'bill'
        ? API_CONFIG.ENDPOINTS.BILLS.GET_BY_ID
        : parent === 'payment'
          ? API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.GET_BY_ID
          : parent === 'bill_payment'
            ? API_CONFIG.ENDPOINTS.BILL_PAYMENTS.GET_BY_ID
            : API_CONFIG.ENDPOINTS.ITEMS.GET_BY_ID;

  return `${base}/${parentId}/attachments`;
};

const parentLinkStagedPath = (parent: AttachmentParentType, parentId: number): string | null => {
  if (parent === 'invoice') {
    return `${API_CONFIG.ENDPOINTS.INVOICES.GET_BY_ID}/${parentId}/attachments/link-staged`;
  }
  if (parent === 'bill') {
    return `${API_CONFIG.ENDPOINTS.BILLS.GET_BY_ID}/${parentId}/attachments/link-staged`;
  }
  if (parent === 'payment') {
    return `${API_CONFIG.ENDPOINTS.CUSTOMER_PAYMENTS.GET_BY_ID}/${parentId}/attachments/link-staged`;
  }
  if (parent === 'bill_payment') {
    return `${API_CONFIG.ENDPOINTS.BILL_PAYMENTS.GET_BY_ID}/${parentId}/attachments/link-staged`;
  }
  return null;
};

export const parseAttachmentErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;
  const fileErrors = errorData.errors?.file;
  const detail =
    Array.isArray(fileErrors) && fileErrors.length > 0 ? `${message} (${fileErrors.join(', ')})` : message;

  console.error('[attachmentApi]', response.status, response.url, errorData);

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(detail, errorData.errors);
  }

  throw new Error(detail);
};

export const getParentAttachments = async (
  parent: AttachmentParentType,
  parentId: number
): Promise<Attachment[]> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${parentAttachmentsPath(parent, parentId)}`, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseAttachmentErrorResponse(response, 'Failed to fetch attachments');
  }

  const result: AttachmentsResponse = await response.json();
  return result.data ?? [];
};

export interface UploadAttachmentOptions {
  note?: string;
  include_on_send?: boolean;
}

const buildUploadFormData = (
  file: File,
  parent: AttachmentParentType,
  options?: UploadAttachmentOptions
): FormData => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  if (options?.note?.trim()) {
    formData.append('note', options.note.trim());
  }

  if (parent === 'invoice' && options?.include_on_send) {
    formData.append('include_on_send', '1');
  }

  return formData;
};

export const uploadParentAttachment = async (
  parent: AttachmentParentType,
  parentId: number,
  file: File,
  options?: UploadAttachmentOptions
): Promise<Attachment> => {
  if (!(file instanceof File) || file.size === 0) {
    throw new Error('No file selected.');
  }

  const response = await fetch(`${API_CONFIG.BASE_URL}${parentAttachmentsPath(parent, parentId)}`, {
    method: 'POST',
    headers: authHeaders(),
    body: buildUploadFormData(file, parent, options),
  });

  if (!response.ok) {
    await parseAttachmentErrorResponse(response, 'Failed to upload attachment');
  }

  const result: AttachmentResponse = await response.json();
  return result.data;
};

export interface UpdateAttachmentMetadataData {
  note?: string | null;
  include_on_send?: boolean;
}

export const updateAttachmentMetadata = async (
  attachmentId: number,
  data: UpdateAttachmentMetadataData
): Promise<Attachment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ATTACHMENTS.GET_BY_ID}/${attachmentId}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseAttachmentErrorResponse(response, 'Failed to update attachment');
  }

  const result: AttachmentResponse = await response.json();
  return result.data;
};

export const deleteAttachment = async (attachmentId: number): Promise<void> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ATTACHMENTS.DELETE}/${attachmentId}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseAttachmentErrorResponse(response, 'Failed to delete attachment');
  }
};

export interface StageAttachmentOptions {
  note?: string;
  intended_attachable_type?: IntendedAttachableType;
  upload_session_id?: string;
  signal?: AbortSignal;
}

export const stageAttachment = async (
  file: File,
  options?: StageAttachmentOptions
): Promise<Attachment> => {
  if (!(file instanceof File) || file.size === 0) {
    throw new Error('No file selected.');
  }

  const formData = new FormData();
  formData.append('file', file, file.name);

  if (options?.note?.trim()) {
    formData.append('note', options.note.trim());
  }
  if (options?.intended_attachable_type) {
    formData.append('intended_attachable_type', options.intended_attachable_type);
  }
  if (options?.upload_session_id) {
    formData.append('upload_session_id', options.upload_session_id);
  }

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ATTACHMENTS.STAGING}`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
      signal: options?.signal,
    }
  );

  if (!response.ok) {
    await parseAttachmentErrorResponse(response, 'Failed to stage attachment');
  }

  const result: AttachmentResponse = await response.json();
  return result.data;
};

export interface ListStagedAttachmentsParams {
  upload_session_id?: string;
  intended_attachable_type?: IntendedAttachableType;
  mine_only?: boolean;
}

export const listStagedAttachments = async (
  params?: ListStagedAttachmentsParams
): Promise<Attachment[]> => {
  const searchParams = new URLSearchParams();
  if (params?.upload_session_id) {
    searchParams.set('upload_session_id', params.upload_session_id);
  }
  if (params?.intended_attachable_type) {
    searchParams.set('intended_attachable_type', params.intended_attachable_type);
  }
  if (params?.mine_only !== undefined) {
    searchParams.set('mine_only', params.mine_only ? '1' : '0');
  }

  const query = searchParams.toString();
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ATTACHMENTS.STAGING}${query ? `?${query}` : ''}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseAttachmentErrorResponse(response, 'Failed to fetch staged attachments');
  }

  const result: AttachmentsResponse = await response.json();
  return result.data ?? [];
};

export interface LinkStagedAttachmentsData {
  staged_attachment_ids: number[];
  include_on_send_by_id?: Record<string, boolean>;
}

export const linkStagedAttachments = async (
  parent: Extract<AttachmentParentType, 'invoice' | 'bill' | 'payment' | 'bill_payment'>,
  parentId: number,
  data: LinkStagedAttachmentsData
): Promise<Attachment[]> => {
  const path = parentLinkStagedPath(parent, parentId);
  if (!path) {
    throw new Error('Link staged is not supported for this parent type.');
  }

  const response = await fetch(`${API_CONFIG.BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseAttachmentErrorResponse(response, 'Failed to link staged attachments');
  }

  const result: AttachmentsResponse = await response.json();
  return result.data ?? [];
};

export const syncStagedAttachmentNotes = async (
  pending: PendingAttachment[]
): Promise<string[]> => {
  const failures: string[] = [];

  await Promise.all(
    pending.map(async (row) => {
      if (!isStagedNoteDirty(row) || !row.attachment) return;
      try {
        await updateAttachmentMetadata(row.attachment.id, {
          note: row.note.trim() || null,
        });
      } catch (err) {
        failures.push(
          `Update ${row.attachment.filename}: ${err instanceof Error ? err.message : 'failed'}`
        );
      }
    })
  );

  return failures;
};

export interface ProcessAttachmentChangesInput {
  parent: AttachmentParentType;
  parentId: number;
  pending: PendingAttachment[];
  existing: EditableAttachment[];
  showIncludeOnSend: boolean;
  onUploadProgress?: (done: number, total: number) => void;
}

export interface ProcessAttachmentChangesResult {
  failures: string[];
}

export const processAttachmentChanges = async (
  input: ProcessAttachmentChangesInput
): Promise<ProcessAttachmentChangesResult> => {
  const failures: string[] = [];

  for (const row of input.existing) {
    if (!row.markedForDelete) continue;
    try {
      await deleteAttachment(row.attachment.id);
    } catch (err) {
      failures.push(
        `Remove ${row.attachment.filename}: ${err instanceof Error ? err.message : 'failed'}`
      );
    }
  }

  for (const row of input.existing) {
    if (row.markedForDelete || !isEditableAttachmentDirty(row)) continue;
    try {
      await updateAttachmentMetadata(row.attachment.id, {
        note: row.note.trim() || null,
        ...(input.showIncludeOnSend ? { include_on_send: row.include_on_send } : {}),
      });
    } catch (err) {
      failures.push(
        `Update ${row.attachment.filename}: ${err instanceof Error ? err.message : 'failed'}`
      );
    }
  }

  const uploadable = input.pending.filter((row) => row.file);
  const total = uploadable.length;
  let done = 0;

  await Promise.all(
    uploadable.map(async (pending) => {
      try {
        await uploadParentAttachment(input.parent, input.parentId, pending.file!, {
          note: pending.note,
          include_on_send: input.showIncludeOnSend ? pending.include_on_send : undefined,
        });
        done += 1;
        input.onUploadProgress?.(done, total);
      } catch (err) {
        failures.push(
          `Upload ${getPendingDisplayName(pending)}: ${err instanceof Error ? err.message : 'failed'}`
        );
      }
    })
  );

  return { failures };
};
